/**
 * KnativeAdminZone - 运维应急区（底部可折叠）
 *
 * 收纳两个「直写」入口（绕过 CD Pipeline 校验，运维应急用）：
 *   - 切换流量：TrafficSplitDialog（merge-PATCH spec.traffic）
 *   - 新建版本：CreateRevisionDialog（PUT 整体替换 + 409 重试）
 *
 * 研发正常发布/切流走卡片主按钮（唤起 CD Pipeline）。
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import BuildIcon from '@material-ui/icons/Build';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  CreateRevisionDialog,
  TrafficSplitDialog,
  useKnativeService,
  useKnativeRevisions,
  KNATIVE_SERVICE_ANNOTATION,
  KNATIVE_NAMESPACE_ANNOTATION,
} from '@internal/plugin-knative-serving';

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1, 1.5),
    color: theme.palette.text.secondary,
    userSelect: 'none',
  },
  body: {
    padding: theme.spacing(0, 2, 2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

export const KnativeAdminZone: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { service, refresh: refreshService } = useKnativeService();
  const { revisions, refresh: refreshRevisions } = useKnativeRevisions();

  const [expanded, setExpanded] = useState(false);
  const [trafficOpen, setTrafficOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const annotations = entity.metadata.annotations || {};
  const serviceName = annotations[KNATIVE_SERVICE_ANNOTATION];
  const serviceNamespace =
    annotations[KNATIVE_NAMESPACE_ANNOTATION] ||
    entity.metadata.namespace ||
    'default';
  const clusterName =
    annotations['backstage.io/kubernetes-cluster-name'] || 'local-cluster';

  const refresh = () => {
    refreshService();
    refreshRevisions();
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.header} onClick={() => setExpanded(!expanded)}>
        <ExpandMoreIcon
          fontSize="small"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
        <BuildIcon fontSize="small" />
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          运维应急
        </Typography>
        <Typography variant="caption" color="textSecondary">
          直接写入集群，绕过 CD Pipeline 校验
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box className={classes.body}>
          <Alert severity="warning">
            以下操作直接写入 Knative Service（不经过工作流的白名单/一致性校验，也不留 PipelineRun 记录）。
            仅限运维应急使用；研发正常发布/切流请使用上方卡片按钮。
          </Alert>
          <Box className={classes.actions}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setTrafficOpen(true)}
            >
              切换流量
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              新建版本
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* 直写对话框 */}
      <TrafficSplitDialog
        open={trafficOpen}
        onClose={() => setTrafficOpen(false)}
        onSaved={refresh}
        service={service}
        serviceName={serviceName}
        serviceNamespace={serviceNamespace}
        clusterName={clusterName}
        revisions={revisions}
      />
      <CreateRevisionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          refresh();
        }}
        service={service}
        revisions={revisions}
        clusterName={clusterName}
        namespace={serviceNamespace}
      />
    </Box>
  );
};
